import './locales/initLocale';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import isToday from 'dayjs/plugin/isToday';

import { CssBaseline } from '@thirdspacelearning/library';
import { MaintenanceProvider } from '@tsl-frontend/tsl-maintenance';
import { LocaleRouterProvider } from '@tsl-frontend/tsl-locale';
import { AuthProvider, MembershipType } from '@tsl-frontend/tsl-keycloak-auth';
import { LaunchDarklyProvider } from '@tsl-frontend/tsl-launch-darkly';
import { queryClient, rudderStackAnalytics } from '@tsl-frontend/tsl-utilities';

import { ThemeSwitch } from './app/theming';

import { environment } from '@environments/environment';
import { appInfo } from '@consts';
import { useLaunchDarklyContext } from '@hooks';
import { routes } from '@routes';
import { store } from '@store';
import { SxSnackbar } from '@shared';

// global dayjs extension setup
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(isToday);

rudderStackAnalytics.init(
    appInfo,
    environment.rudderStackWriteKey,
    environment.rudderStackDataPlane,
    environment.rudderstackApiUrl,
);

interface IWindow extends Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Cypress: any;
}

const main = (
    <Provider store={store}>
        <QueryClientProvider client={queryClient}>
            <AuthProvider
                keycloakUrl={environment.keycloakUrl}
                keycloakRealm={environment.keycloakRealm}
                keycloakClientId={environment.keycloakClientId}
                allowedMemberships={[MembershipType.STUDENT]}
            >
                <LaunchDarklyProvider
                    clientSideID={environment.launchDarklyKey}
                    useLaunchDarklyContext={useLaunchDarklyContext}
                >
                    <ThemeSwitch>
                        <CssBaseline />
                        <SnackbarProvider
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            Components={{
                                sxSnackbar: SxSnackbar,
                            }}
                            autoHideDuration={3000}
                            preventDuplicate={true}
                        />
                        <MaintenanceProvider>
                            <LocaleRouterProvider routes={routes} />
                        </MaintenanceProvider>
                    </ThemeSwitch>
                </LaunchDarklyProvider>
            </AuthProvider>
        </QueryClientProvider>
    </Provider>
);

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
    /**
     * don't render strict mode in production configurations
     * react-player does not currently handle this dev mode well
     * should do this automatically but may need to upgrade nx to recognise react 18 fully
     */
    (environment.production && main) ||
        ((window as IWindow & typeof globalThis).Cypress && main) || (
            <React.StrictMode>{main}</React.StrictMode>
        ),
);
